export interface FilterResponse {
  success: true;
  data: FilterData;
  meta: ResponseMeta;
}

export interface FilterErrorResponse {
  success: false;
  error: {
    code: string;
    message: string;
    details: {
      missing_fields?: string[];
      invalid_fields?: string[];
      action_id?: string;
      category_id?: string;
      suggestion?: string;
    };
  };
}

export interface FilterData {
  meta: FilterMeta;
  groups: FilterGroup[];
}

export interface FilterMeta {
  action: ActionInfo;
  category: CategoryInfo;
  total_groups: number;
  total_options: number;
  generated_at: string; // ISO 8601 datetime
  cache_key: string;
}

export interface ActionInfo {
  id: number;
  name: string;
  name_th: string;
  name_en: string;
  name_cn: string;
}

export interface CategoryInfo {
  id: number;
  name: string;
  name_th: string;
  name_en: string;
  name_cn: string;
}

export interface FilterGroup {
  group_id: number;
  group_name: string;
  group_name_th: string;
  group_name_en: string;
  group_name_cn: string;
  order: number;
  options: FilterOption[];
}

export interface FilterOption {
  id: number;
  name: string;
  name_th: string;
  name_en: string;
  name_cn: string;
  query_params: Record<string, any>; // Dynamic object
  query_string: string;
  order: number;
}

export interface ResponseMeta {
  timestamp: string; // ISO 8601 datetime
  response_time_ms: number;
  version: string; // "1.0.0"
}

export type FiltersApiResponse = FilterResponse | FilterErrorResponse;

const ACTION_MAPPINGS = {
  2: 'โครงการ',
  3: 'ขาย', 
  4: 'ให้เช่า',
  5: 'เซ้ง',
  6: 'จำนองขายฝาก'
} as const;

const CATEGORY_MAPPINGS = {
  1: 'คอนโด',
  2: 'ทาวน์เฮาส์-โฮม', // ใช้ชื่อเต็มตามจริง
  3: 'บ้านเดี่ยว',
  4: 'โฮมออฟฟิศ', // ใช้ ID 4 สำหรับโฮมออฟฟิศ
  5: 'ที่ดิน',
  6: 'พื้นที่สำนักงาน', // ใช้ชื่อเต็มตามจริงจาก API
  7: 'อพาร์ทเมนท์',
  9: 'อาคารพาณิชย์',
  10: 'ร้านค้า',
  11: 'โรงแรม', // โรงแรมคือ ID 11 ตามข้อมูลจริง
  13: 'เซอร์วิสอพาร์ทเมนท์', 
  15: 'อาคารสำนักงาน',
  16: 'โรงงาน',
  17: 'รีสอร์ท',
  24: 'บ้านแฝด',
  999: 'อสังหา' // ใช้เป็น catch-all category
} as const;

const ACTION_MULTI_LANG_MAPPINGS = {
  2: {
    th: 'โครงการ',
    en: 'Project',
    cn: '项目'
  },
  3: {
    th: 'ขาย',
    en: 'Sell',
    en_alt: 'For Sale', // เพิ่มเพื่อรองรับ UI ที่ส่ง 'For Sale'
    cn: '出售'
  },
  4: {
    th: 'ให้เช่า',
    en: 'Rent',
    en_alt: 'For Rent', // เพิ่มเพื่อรองรับ UI ที่ส่ง 'For Rent'
    cn: '出租'
  },
  5: {
    th: 'เซ้ง',
    en: 'Transfer',
    cn: '转让'
  },
  6: {
    th: 'จำนองขายฝาก',
    en: 'Provide Property Mortgage Loan',
    cn: '提供房地产抵押贷款'
  }
} as Record<number, Record<string, string>>;

const CATEGORY_MULTI_LANG_MAPPINGS = {
  1: {
    th: 'คอนโด',
    en: 'Condo',
    cn: '公寓'
  },
  2: {
    th: 'ทาวน์เฮาส์-โฮม',
    en: 'Townhouse',
    cn: '联排别墅'
  },
  3: {
    th: 'บ้านเดี่ยว',
    en: 'Detached House',
    cn: '独栋别墅'
  },
  4: {
    th: 'โฮมออฟฟิศ',
    en: 'Home Office',
    cn: '居家办公'
  },
  5: {
    th: 'ที่ดิน',
    en: 'Land',
    cn: '土地'
  },
  6: {
    th: 'พื้นที่สำนักงาน',
    en: 'Office Space',
    cn: '办公空间'
  },
  7: {
    th: 'อพาร์ทเมนท์',
    en: 'Apartment',
    cn: '公寓'
  },
  9: {
    th: 'อาคารพาณิชย์',
    en: 'Commercial Building',
    cn: '商业建筑'
  },
  10: {
    th: 'ร้านค้า',
    en: 'Shop',
    cn: '商店'
  },
  11: {
    th: 'โรงแรม',
    en: 'Hotel',
    cn: '酒店'
  },
  13: {
    th: 'เซอร์วิสอพาร์ทเมนท์',
    en: 'Service Apartment',
    cn: '服务式公寓'
  },
  15: {
    th: 'อาคารสำนักงาน',
    en: 'Office Building',
    cn: '办公楼'
  },
  16: {
    th: 'โรงงาน',
    en: 'Factory',
    cn: '工厂'
  },
  17: {
    th: 'รีสอร์ท',
    en: 'Resort',
    cn: '度假型酒店'
  },
  24: {
    th: 'บ้านแฝด',
    en: 'Twin house',
    cn: '联体屋'
  },
  999: {
    th: 'อสังหา',
    en: 'Real Estate',
    cn: '房地产'
  }
} as const;

const ACTION_NAME_TO_ID = (() => {
  const mapping: Record<string, number> = {};
  
  Object.entries(ACTION_MAPPINGS).forEach(([id, name]) => {
    mapping[name] = Number(id);
  });
  
  Object.entries(ACTION_MULTI_LANG_MAPPINGS).forEach(([id, names]) => {
    Object.entries(names).forEach(([key, name]) => {
      mapping[name] = Number(id);
    });
  });
  
  return mapping;
})();

const CATEGORY_NAME_TO_ID = (() => {
  const mapping: Record<string, number> = {};
  
  Object.entries(CATEGORY_MAPPINGS).forEach(([id, name]) => {
    mapping[name] = Number(id);
  });
  
  Object.entries(CATEGORY_MULTI_LANG_MAPPINGS).forEach(([id, names]) => {
    Object.values(names).forEach(name => {
      mapping[name] = Number(id);
    });
  });
  
  return mapping;
})();

export class FiltersService {
  private static readonly BASE_URL = process.env.BACK_END_URL || process.env.NEXT_PUBLIC_BACK_END_URL || 'http://localhost:3030';
  private static readonly ENDPOINT = '/filters/complete';

  /**
   * เรียก API เพื่อดึงข้อมูล filters
   */
  static async getCompleteFilters(
    actionId: number,
    categoryId: number,
    lang: string = 'th'
  ): Promise<FilterResponse> {
    try {
      // ถ้า category_id === 999 ให้ return empty response เลยไม่ต้อง fetch เพราะเป็น id ที่ไม่มีอยู่จริง
      if (categoryId === 999) {
        return {
          success: true,
          data: {
            meta: {
              action: {
                id: actionId,
                name: '',
                name_th: '',
                name_en: '',
                name_cn: ''
              },
              category: {
                id: categoryId,
                name: '',
                name_th: '',
                name_en: '',
                name_cn: ''
              },
              total_groups: 0,
              total_options: 0,
              generated_at: new Date().toISOString(),
              cache_key: `filters:${actionId}:${categoryId}:${lang}`
            },
            groups: []
          },
          meta: {
            timestamp: new Date().toISOString(),
            response_time_ms: 0,
            version: "1.0.0"
          }
        };
      }

      const url = new URL(`${this.BASE_URL}${this.ENDPOINT}`);
      url.searchParams.append('action_id', actionId.toString());
      url.searchParams.append('category_id', categoryId.toString());
      url.searchParams.append('lang', lang);

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        if (response.status === 404) {
          throw new Error(`ไม่พบข้อมูล filter สำหรับ action_id=${actionId} และ category_id=${categoryId}`);
        }
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: FiltersApiResponse = await response.json();

      if (!data.success) {
        const errorResponse = data as FilterErrorResponse;
        throw new Error(errorResponse.error.message || 'API Error');
      }

      return data as FilterResponse;
    } catch (error) {
      console.error('FiltersService.getCompleteFilters error:', error);
      throw error;
    }
  }

  /**
   * แปลงชื่อ action เป็น ID
   */
  static mapActionToId(actionName: string): number | null {
    const actionId = ACTION_NAME_TO_ID[actionName] || null;
    return actionId;
  }

  /**
   * แปลงชื่อ category เป็น ID  
   */
  static mapCategoryToId(categoryName: string): number | null {
    const categoryId = CATEGORY_NAME_TO_ID[categoryName] || null;
    return categoryId;
  }

  /**
   * แปลง ID เป็นชื่อ action
   */
  static mapIdToAction(actionId: number): string | null {
    return ACTION_MAPPINGS[actionId as keyof typeof ACTION_MAPPINGS] || null;
  }

  /**
   * แปลง ID เป็นชื่อ category
   */
  static mapIdToCategory(categoryId: number): string | null {
    return CATEGORY_MAPPINGS[categoryId as keyof typeof CATEGORY_MAPPINGS] || null;
  }

  /**
   * ตรวจสอบว่า action ID ถูกต้องหรือไม่
   */
  static isValidActionId(actionId: number): boolean {
    return actionId in ACTION_MAPPINGS;
  }

  /**
   * ตรวจสอบว่า category ID ถูกต้องหรือไม่
   */
  static isValidCategoryId(categoryId: number): boolean {
    return categoryId in CATEGORY_MAPPINGS;
  }

  /**
   * ดึงรายการ actions ทั้งหมด
   */
  static getAllActions(): Array<{ id: number; name: string }> {
    return Object.entries(ACTION_MAPPINGS).map(([id, name]) => ({
      id: Number(id),
      name
    }));
  }

  /**
   * ดึงรายการ categories ทั้งหมด
   */
  static getAllCategories(): Array<{ id: number; name: string }> {
    return Object.entries(CATEGORY_MAPPINGS).map(([id, name]) => ({
      id: Number(id),
      name
    }));
  }

  /**
   * สร้าง query string จาก filter options ที่เลือก
   */
  static buildQueryString(selectedOptions: FilterOption[]): string {
    const params = new URLSearchParams();
    
    selectedOptions.forEach(option => {
      Object.entries(option.query_params).forEach(([key, value]) => {
        if (value !== null && value !== undefined) {
          params.append(key, String(value));
        }
      });
    });

    return params.toString();
  }

  /**
   * รวม query params จาก multiple options
   */
  static mergeQueryParams(selectedOptions: FilterOption[]): Record<string, any> {
    const merged: Record<string, any> = {};
    
    selectedOptions.forEach(option => {
      Object.assign(merged, option.query_params);
    });

    return merged;
  }

  /**
   * ฟิลเตอร์ options ตามคำค้นหา
   */
  static filterOptions(
    groups: FilterGroup[], 
    searchTerm: string, 
    lang: string = 'th'
  ): FilterGroup[] {
    if (!searchTerm.trim()) return groups;

    const searchLower = searchTerm.toLowerCase();
    
    return groups.map(group => ({
      ...group,
      options: group.options.filter(option => {
        const nameField = lang === 'en' ? 'name_en' : 
                         lang === 'cn' ? 'name_cn' : 'name_th';
        return option[nameField].toLowerCase().includes(searchLower);
      })
    })).filter(group => group.options.length > 0);
  }

  /**
   * จัดกลุ่ม options ตาม group
   */
  static groupOptionsByCategory(groups: FilterGroup[]): Record<string, FilterOption[]> {
    const grouped: Record<string, FilterOption[]> = {};
    
    groups.forEach(group => {
      grouped[group.group_name] = group.options;
    });

    return grouped;
  }

  /**
   * ฟังก์ชันทดสอบ mapping (ใช้เพื่อ debug)
   */
  static testMappings() {
    const testCases = [
      { name: 'โรงแรม', expected: 11, lang: 'th' },
      { name: 'อสังหา', expected: 999, lang: 'th' },
      { name: 'คอนโด', expected: 1, lang: 'th' },
      { name: 'ขาย', expected: 3, lang: 'th' },
      { name: 'ให้เช่า', expected: 4, lang: 'th' },
      
      { name: 'Hotel', expected: 11, lang: 'en' },
      { name: 'Condo', expected: 1, lang: 'en' },
      { name: 'Sell', expected: 3, lang: 'en' },
      { name: 'Rent', expected: 4, lang: 'en' },
      
      { name: '酒店', expected: 11, lang: 'cn' },
      { name: '公寓', expected: 1, lang: 'cn' },
      { name: '出售', expected: 3, lang: 'cn' },
      { name: '出租', expected: 4, lang: 'cn' }
    ];
    
    testCases.forEach(testCase => {
      const isCategoryTest = testCase.expected <= 999 && testCase.expected !== 3 && testCase.expected !== 4;
      const result = isCategoryTest 
        ? this.mapCategoryToId(testCase.name)
        : this.mapActionToId(testCase.name);
      const type = isCategoryTest ? 'Category' : 'Action';
    });
    
    return {
      categories: CATEGORY_NAME_TO_ID,
      actions: ACTION_NAME_TO_ID,
      multiLangActions: ACTION_MULTI_LANG_MAPPINGS,
      multiLangCategories: CATEGORY_MULTI_LANG_MAPPINGS
    };
  }
} 